/**
 * Settings store — UI signal for the Settings panel's open/close
 * state. Lives outside the panel component so any callsite can open
 * it (header gear, future from-error toast, command palette, etc.)
 * without prop-drilling.
 *
 * Phase 1 of the panel only ships the Backup section. Ports, TouchOSC
 * Importer, etc. will land alongside this signal as additional
 * sections; the open/close mechanism is shared.
 */

import { createSignal } from 'solid-js';
import { connect as connectTrafficMonitor, disconnect as disconnectTrafficMonitor } from '../opbridge/trafficMonitor.ts';

const [open, setOpen] = createSignal(false);

export const settingsOpen = open;

/** The Settings sections a caller can land on - each is a `data-section`
 *  on its `<section>` in SettingsPanel.tsx. Navigate's Look / Backup cards and
 *  its search reach a section directly instead of dropping the operator at the
 *  top of an eight-section scroll to hunt for it. */
export type SettingsSection =
  | 'connection' | 'diagnostics' | 'interface' | 'preflight'
  | 'panic' | 'panic-midi-trigger' | 'export' | 'import' | 'about';

const [focusSection, setFocusSection] = createSignal<SettingsSection | null>(null);
/** The section Settings should scroll to when it mounts. SettingsPanel reads
 *  it once and clears it, so a later plain open lands at the top again. */
export const settingsFocusSection = focusSection;
export function clearSettingsFocusSection(): void { setFocusSection(null); }

/** Which of Navigate's three places Settings was opened for (2026-10-05,
 *  app-study.html): Look (skins, bar, dim), Backup (export, import) or the
 *  rest. Settings opens INSIDE Navigate now, as one of those panes. */
export type SettingsPane = 'settings' | 'look' | 'backup';
const [pane, setPane] = createSignal<SettingsPane>('settings');
export const settingsPane = pane;
function paneFor(section: SettingsSection | null | undefined): SettingsPane {
  if (section === 'interface') return 'look';
  if (section === 'export' || section === 'import') return 'backup';
  return 'settings';
}

export function openSettings(section?: SettingsSection): void {
  setFocusSection(section ?? null);
  setPane(paneFor(section));
  setOpen(true);
}
/** Open one of the three Settings places by name (Navigate's list). */
export function openSettingsPane(p: SettingsPane): void {
  setFocusSection(null);
  setPane(p);
  setOpen(true);
}
export function closeSettings(): void { setOpen(false); }

// ─── Connect popup (OPBridge pairing surface) ──────────────────────
// The Connect surface used to live inline as the first Settings section
// (DEC-060). It now opens as its own modal: the header OPBridge pill
// lands straight on it, and a shortcut inside Settings opens it too.
// Kept as a sibling signal so it can stack over Settings (opened from
// the shortcut) or stand alone (opened from the header pill).

const [connect, setConnect] = createSignal(false);

export const connectOpen = connect;

export function openConnect(): void { setConnect(true); }
export function closeConnect(): void { setConnect(false); }

// ─── Connected Devices (guitar-effects pedal registry, DEC-152) ───
// Moved out of Settings 2026-08-04 into its own modal off the logo/Navigate
// menu — pedals are project-scoped rig truth (same category as Timelines/
// Matrix, which already live there), not a per-browser Settings preference.
// Same sibling-signal shape as Connect: a standalone destination, not a
// Settings sub-section.

const [connectedDevices, setConnectedDevices] = createSignal(false);

export const connectedDevicesOpen = connectedDevices;

export function openConnectedDevices(): void { setConnectedDevices(true); }
export function closeConnectedDevices(): void { setConnectedDevices(false); }

// ─── Traffic Monitor (diagnostic — live inbound MIDI/OSC, DEC pending) ───
// Standalone modal, same sibling-signal shape as Connect/Connected Devices,
// reached via a shortcut row inside Settings. Open/close directly drive
// `opbridge/trafficMonitor.ts`'s connect()/disconnect() — the WS sockets +
// ring buffers only exist while this modal is actually on screen (see that
// module's header for why).

const [trafficMonitor, setTrafficMonitor] = createSignal(false);

export const trafficMonitorOpen = trafficMonitor;

export function openTrafficMonitor(): void { setTrafficMonitor(true); connectTrafficMonitor(); }
export function closeTrafficMonitor(): void { setTrafficMonitor(false); disconnectTrafficMonitor(); }

// ─── Chrome bar position (top / bottom) ────────────────────────────
// The single chrome bar (was header + bottom bar; the bottom bar is
// retired) can sit at the top or bottom of the shell. Per-device UI
// preference, persisted in localStorage - iPad and laptop each keep
// their own. Read by App.tsx (sets `data-bar-position` on .shell).

export type BarPosition = 'top' | 'bottom';

const BAR_POS_KEY = 'opbuttons.barPosition.v1';

function loadBarPosition(): BarPosition {
  if (typeof localStorage === 'undefined') return 'top';
  return localStorage.getItem(BAR_POS_KEY) === 'bottom' ? 'bottom' : 'top';
}

const [barPos, setBarPos] = createSignal<BarPosition>(loadBarPosition());
/** Where the chrome bar sits: 'top' (default) or 'bottom'. */
export const barPosition = barPos;

export function setBarPosition(pos: BarPosition): void {
  setBarPos(pos);
  try { localStorage.setItem(BAR_POS_KEY, pos); } catch { /* private mode / quota */ }
}

// ─── Skin (DEC-121 / 126 / 130) ────────────────────────────────────
// Which of the seven skins drives the app. Per-device like the other
// interface prefs: the iPad on stage and the laptop at the desk can
// legitimately want different looks (one is viewed at arm's length under
// stage wash, the other at 60cm in a lit room).
//
// DEFAULT IS OBSIDIAN, by operator decision 2026-07-28. Note the consequence
// for anyone reading a stale doc: DEC-121 shipped "Neon only" earlier the
// same day and SKIN-SYSTEM.md's build order still described Neon as the one
// wired in. Obsidian superseded it once all seven landed.

export type SkinName =
  | 'neon' | 'phosphor' | 'vector' | 'blacklight' | 'thermal' | 'machined' | 'obsidian';

export const SKINS: ReadonlyArray<{ id: SkinName; name: string; blurb: string }> = [
  { id: 'obsidian',   name: 'Obsidian',   blurb: 'Polished black glass, chrome, white LED' },
  { id: 'machined',   name: 'Machined',   blurb: 'Anodised aluminium, warm backlight' },
  { id: 'neon',       name: 'Neon',       blurb: 'Electric cyan tube on blue-black' },
  { id: 'phosphor',   name: 'Phosphor',   blurb: 'CRT glass, scanlines, mono type' },
  { id: 'vector',     name: 'Vector',     blurb: 'Oscilloscope stroke, nothing solid until it fires' },
  { id: 'blacklight', name: 'Blacklight', blurb: 'UV violet room, fluoro live' },
  { id: 'thermal',    name: 'Thermal',    blurb: 'State as temperature, cold to white-hot' },
];

const SKIN_KEY = 'opbuttons.skin.v1';
const DEFAULT_SKIN: SkinName = 'obsidian';

function loadSkin(): SkinName {
  if (typeof localStorage === 'undefined') return DEFAULT_SKIN;
  const raw = localStorage.getItem(SKIN_KEY);
  // Validate against the known set rather than trusting storage: a hand-edited
  // or stale value would otherwise put `data-skin="whatever"` on the shell,
  // match no rule, and silently fall back to the base tokens - which looks
  // like "the skin system is broken" rather than "that skin does not exist".
  return SKINS.some((s) => s.id === raw) ? (raw as SkinName) : DEFAULT_SKIN;
}

const [skinSig, setSkinSig] = createSignal<SkinName>(loadSkin());
/** The active skin (DEC-130). Drives `data-skin` on the shell. */
export const activeSkin = skinSig;

export function setActiveSkin(skin: SkinName): void {
  setSkinSig(skin);
  try { localStorage.setItem(SKIN_KEY, skin); } catch { /* private mode / quota */ }
}

// ─── Triggers in edit mode ─────────────────────────────────────────
// Default OFF: in edit mode a tap selects / drags the item and controls do
// NOT fire (the long-standing behaviour). Turned ON, controls FIRE on tap
// in edit mode (so you can test them in place); to move an item you then
// hover it for ~2s to "grab" it (hand cursor, trigger suppressed while
// grabbed, draggable). Per-device, persisted.

const EDIT_TRIGGERS_KEY = 'opbuttons.editTriggers.v1';

function loadEditTriggers(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(EDIT_TRIGGERS_KEY) === 'on';
}

const [editTrig, setEditTrig] = createSignal<boolean>(loadEditTriggers());
/** Whether controls fire on tap while in edit mode (default false). */
export const editTriggersEnabled = editTrig;

export function setEditTriggersEnabled(on: boolean): void {
  setEditTrig(on);
  try { localStorage.setItem(EDIT_TRIGGERS_KEY, on ? 'on' : 'off'); } catch { /* private mode / quota */ }
}

/** ms of mouse-hover over an item (in edit mode, triggers-on) before it
 *  "grabs" for dragging. */
export const EDIT_GRAB_HOVER_MS = 2000;

// ─── Snap while moving (2026-10-10) ───────────────────────────────
// Default ON: a dragged or resized control snaps to its neighbours' edges and
// centres, to the standard gap beside them, and to a 10px grid
// (`layout/placement.ts`). Off = free placement. Alt held during a drag
// skips it on a desktop either way. Per-device, persisted.

const SNAP_KEY = 'opbuttons.snap.v1';

function loadSnap(): boolean {
  if (typeof localStorage === 'undefined') return true;
  return localStorage.getItem(SNAP_KEY) !== 'off';
}

const [snapSig, setSnapSig] = createSignal<boolean>(loadSnap());
/** Whether dragging and resizing snap (default true). */
export const snapEnabled = snapSig;

export function setSnapEnabled(on: boolean): void {
  setSnapSig(on);
  try { localStorage.setItem(SNAP_KEY, on ? 'on' : 'off'); } catch { /* private mode / quota */ }
}

// SPACE BETWEEN CONTROLS (2026-10-10, operator: "there should be a
// changeable setting for padding a moved item, default should be 0"). The gap
// a moved control snaps to beside a level neighbour, the clearance a drop
// settles at, and how far beside the last control a new one is placed
// (layout/placement.ts `gap`). Was a fixed 20px (DEC-267). Per device.
const GAP_KEY = 'opbuttons.controlGap.v1';
export const CONTROL_GAP_MAX = 100;
function loadGap(): number {
  try {
    const n = Number(localStorage.getItem(GAP_KEY));
    return Number.isFinite(n) ? Math.max(0, Math.min(CONTROL_GAP_MAX, Math.round(n))) : 0;
  } catch { return 0; }
}
const [gapSig, setGapSig] = createSignal<number>(loadGap());
/** Space kept between controls when moving and adding them, in canvas px (default 0). */
export const controlGap = gapSig;
export function setControlGap(px: number): void {
  const v = Number.isFinite(px) ? Math.max(0, Math.min(CONTROL_GAP_MAX, Math.round(px))) : 0;
  setGapSig(v);
  try { if (v === 0) localStorage.removeItem(GAP_KEY); else localStorage.setItem(GAP_KEY, String(v)); } catch { /* private mode / quota */ }
}
